import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import EmptyResult from '@mulesoft/anypoint-components/lib/EmptyResult';
import { AssetPropType } from '@mulesoft/exchange-react-shapes';
import InfiniteScroll from '~/components/Layout/InfiniteScroll';
import * as objectUtils from '~/utils/objects';
import { getIconURL } from '~/utils/routes';
import styles from './AssetCardList.css';
import AssetCardWrapper from './AssetCardWrapper';

const MULESOFT_CERTIFIED_TAG = 'mulesoft-certified';
const VirtualScroll = loadable(() =>
  import(
    /* webpackChunkName: "virtual-scroll-list" */ '../../Layout/VirtualScroll'
  )
);

VirtualScroll.displayName = 'VirtualScroll';

class AssetCardList extends React.Component {
  static propTypes = {
    assets: PropTypes.arrayOf(AssetPropType),
    hasMoreAssets: PropTypes.bool,
    isVirtualListEnabled: PropTypes.bool,
    isFetchingAssets: PropTypes.bool,
    onAssetClick: PropTypes.func,
    onLoadAssets: PropTypes.func.isRequired,
    onReachEnd: PropTypes.func,
    query: PropTypes.object,
    searchTerm: PropTypes.string
  };

  static defaultProps = { isVirtualListEnabled: false, searchTerm: '' };

  cardMeasures = [];

  loadMoreAssets = () => {
    const query = {
      offset: this.props.assets.length,
      ...this.props.query
    };

    this.props.onReachEnd();

    return this.props.onLoadAssets(query);
  };

  renderAssetCard = ({ measure, ...assetProps }, index) => {
    const { onAssetClick } = this.props;
    const key = assetProps.id || index;

    return (
      <AssetCardWrapper
        {...assetProps}
        onAssetClick={onAssetClick}
        key={key}
        onVisualUpdate={measure}
        startExpanded={index === 0}
        resultPosition={index + 1}
      />
    );
  };

  render() {
    const {
      assets,
      searchTerm,
      isFetchingAssets,
      isVirtualListEnabled,
      hasMoreAssets
    } = this.props;

    if (assets.length === 0 && !isFetchingAssets) {
      return (
        <div role="alert">
          <EmptyResult
            className={styles.emptyResult}
            message="We couldn't find results that match your criteria"
            testId="assets-empty-result"
          />
        </div>
      );
    }

    let ListView;

    if (isVirtualListEnabled) {
      ListView = VirtualScroll;
    } else {
      ListView = InfiniteScroll;
    }

    const assetData = assets.map(({ highlight, ...asset }) => ({
      ...objectUtils.pick(
        asset,
        'groupId',
        'assetId',
        'name',
        'description',
        'minorVersionModifiedAt',
        'minorVersion',
        'numberOfRates',
        'rating',
        'type',
        'organization'
      ),
      icon: getIconURL(asset),
      createdBy: asset.createdBy
        ? `${asset.createdBy.firstName} ${asset.createdBy.lastName}`
        : '-',
      isCertified: (asset.labels || []).some(
        (label) => label === MULESOFT_CERTIFIED_TAG
      ),
      useSavedState: isVirtualListEnabled,
      searchTerm,
      highlight
    }));

    return (
      <div
        role="list"
        data-test-id="asset-card-list"
        className={classNames({ [styles.virtualList]: isVirtualListEnabled })}
      >
        <ListView
          hasMore={hasMoreAssets}
          onLoadMore={this.loadMoreAssets}
          loadingMoreMessage="Loading more assets ..."
          loadMoreMessage="Load more assets"
          testId="assets"
          renderItem={this.renderAssetCard}
          items={assetData}
          estimatedItemSize={92.222}
        />
      </div>
    );
  }
}

export default AssetCardList;
